import type {
  WCardTemplateElementView,
  WProjectCustomizationElementView,
} from '@zola/svc-web-api-ts-client';

/**
 * A customization or template element, where we have not yet determined if it is
 * an image, or a text element.  Once determined, we can make further inferences
 * about the type: text elements must have a font and image elements must have
 * an image_id.
 */
export type ElementView = WCardTemplateElementView | WProjectCustomizationElementView;

/**
 * Determines if an ElementView is a TextElementView.
 *
 * @example
 * isTextElementView(element);
 */
export const isTextElementView = (element: ElementView): element is TextElementView => {
  return element.content_type === 'TEXT';
};

/**
 * Determines if an ElementView is a ImageElementView.
 *
 * @example
 * isImageElementView(element);
 */
export const isImageElementView = (element: ElementView): element is ImageElementView => {
  return element.content_type === 'IMAGE';
};

/**
 * A Image WProjectCustomizationElementView but with x/y/width/height/image_id required
 *
 * This is used in the customization view
 */
export type ProjectCustomizationImageElementView = WProjectCustomizationElementView &
  Required<Pick<WProjectCustomizationElementView, 'width' | 'height' | 'x' | 'y' | 'image_id'>> & {
    content_type: 'IMAGE';
  };

/**
 * A Image WCardTemplateElementView but with x/y/width/height/image_id required
 *
 * This is used in the customization view
 */
export type CardTemplateImageElementView = WCardTemplateElementView &
  Required<Pick<WCardTemplateElementView, 'width' | 'height' | 'x' | 'y' | 'image_id'>> & {
    content_type: 'IMAGE';
  };

export type ImageElementView = ProjectCustomizationImageElementView | CardTemplateImageElementView;
export type CustomizableImageElementView = ImageElementView & { customizable: true };

export const isCustomImageElementView = (
  element: ImageElementView
): element is CustomizableImageElementView => {
  return Boolean(element.customizable);
};

export const isProjectCustomizationImageElementView = (
  textElement: ImageElementView
): textElement is ProjectCustomizationImageElementView => {
  return 'horizontal_alignment' in textElement;
};

export const isCardTemplateImageElementView = (
  textElement: ImageElementView
): textElement is CardTemplateImageElementView => {
  return !('horizontal_alignment' in textElement);
};

/**
 * A Text WProjectCustomizationElementView but with font/x/y/width/height required
 *
 * This is used in the customization view
 */
export type ProjectCustomizationTextElementView = WProjectCustomizationElementView &
  Required<Pick<WProjectCustomizationElementView, 'font' | 'width' | 'height' | 'x' | 'y'>> & {
    content_type: 'TEXT';
  };

/**
 * Text WCardTemplateElementView but with font/x/y/width/height required
 *
 * This is used on the PDP
 */
export type CardTemplateTextElementView = WCardTemplateElementView &
  Required<Pick<WCardTemplateElementView, 'font' | 'width' | 'height' | 'x' | 'y'>> & {
    content_type: 'TEXT';
    uuid?: string; // WCardTemplateElementView doesn't have a uuid, but we need it in the TextElementView union below
  };

export type TextElementView = ProjectCustomizationTextElementView | CardTemplateTextElementView;

export type CurvedTextElementView = TextElementView &
  Required<Pick<TextElementView, 'svg_curve_path'>>;

export const isProjectCustomizationTextElementView = (
  textElement: TextElementView
): textElement is ProjectCustomizationTextElementView => {
  return 'horizontal_alignment' in textElement;
};

export const isCardTemplateTextElementView = (
  textElement: TextElementView
): textElement is CardTemplateTextElementView => {
  return !('horizontal_alignment' in textElement);
};

export const isCurvedTextElementView = (
  element: TextElementView
): element is CurvedTextElementView => {
  return Boolean(element.svg_curve_path);
};
